import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Eye, Layers } from 'lucide-react';
import { CopyableId } from '@fiado/react-kit';
import { platformApi } from '../../api/endpoints/platform';
import { useAuth } from '../../hooks/useAuth';
import { useTenantName } from '../../hooks/useTenantName';

const DRILL_DOWN_PATTERN = /^\/platform\/tenants\/([^/]+)/;

/** tenantId del drill-down de plataforma en la ruta actual, si lo hay. */
export function drillDownTenantId(pathname: string): string | undefined {
    const id = DRILL_DOWN_PATTERN.exec(pathname)?.[1];
    return id ? decodeURIComponent(id) : undefined;
}

/** Nombre del tenant del drill-down; comparte caché con la ficha del tenant (sin request extra). */
function useViewedTenantName(tenantId: string | undefined): string | undefined {
    const { data } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId ?? ''),
        enabled: !!tenantId,
    });
    return data?.displayName || tenantId;
}

/** Bloque fijo del sidebar: a qué organización inició sesión el usuario (o Plataforma) y qué tenant ve. */
export function SessionContext({ pathname }: { pathname: string }) {
    const { t } = useTranslation(['common']);
    const { user, isPlatform } = useAuth();
    const tenantName = useTenantName();
    const viewedId = isPlatform ? drillDownTenantId(pathname) : undefined;
    const viewedName = useViewedTenantName(viewedId);
    if (!user) return null;

    const name = isPlatform ? t('common:session.platform') : (tenantName ?? user.tenantId);
    const Icon = isPlatform ? Layers : Building2;
    return (
        <section
            aria-label={t('common:session.label')}
            className="shrink-0 space-y-1 border-b border-border px-4 py-3 text-left"
        >
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {t('common:session.label')}
            </p>
            <div className="flex min-w-0 items-center gap-2" title={name}>
                <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate text-sm font-semibold">{name}</span>
            </div>
            {!isPlatform && (
                <div className="min-w-0 text-xs [overflow-wrap:anywhere]">
                    <CopyableId label={t('common:session.tenantId')} value={user.tenantId} />
                </div>
            )}
            {viewedId && (
                <div
                    className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground"
                    title={viewedName}
                >
                    <Eye className="size-3.5 shrink-0" aria-hidden />
                    <span className="shrink-0">{t('common:session.viewing')}</span>
                    <span className="truncate font-medium text-foreground">{viewedName}</span>
                </div>
            )}
        </section>
    );
}
